/**
 * V2 Conformance — legacy↔canonical rule map (SINGLE SOURCE OF TRUTH)
 *
 * Both the strict validator (`validateStepContentV2`) and the repair normalizer
 * (`normalizeStepContentV2`) consume THESE declarations — a unit test asserts the
 * two stay in lock-step (AC-002). The human-readable mirror of this file is
 * `modules/schema-v2/v2-conformance-migration-table.md` (AC-001).
 *
 * Every rule cites the source line that proves the legacy shape exists in the
 * wild — almost always a renderer `?? legacyPath` fallback (the renderer is
 * deliberately tolerant, which is exactly why a broken screen still renders).
 *
 * Two rule families:
 *   1. PATH rules    — a specific, localized structural drift (the progress
 *      indicator object merge + its flat-field moves).
 *   2. SHAPE rules   — drift that recurs at MANY paths (asset origin→original,
 *      enum renames, styles→appearance). Matched by object shape during a tree
 *      walk, the way ScreenStructNormalizer matches V1 structs — so we never
 *      have to enumerate every host path.
 *
 * @module screen-utils/v2-conformance
 */

// ── Rule kinds ──────────────────────────────────────────────────────────────

export interface RuleMeta {
  /** Stable id for reporting + tests. */
  id: string;
  /** Human concept label. */
  concept: string;
  /** Source proof that the legacy shape exists (file:line). */
  evidence: string;
  /** StepKinds this applies to (advisory; omit = all kinds). */
  appliesTo?: string[];
}

/**
 * A scalar leaf whose canonical home is `base.canonicalLeaf` but which legacy
 * data parks at `base.legacyLeaves[i]`. Migrate value→canonical, delete legacy.
 */
export interface LeafMoveRule extends RuleMeta {
  kind: 'leafMove';
  /** Pattern (may contain `[]` / `*`) resolving to the owning object(s). */
  base: string;
  /** Canonical leaf subpath under base. */
  canonicalLeaf: string;
  /** Legacy leaf subpaths under base, in priority order. */
  legacyLeaves: string[];
}

/**
 * Sibling legacy objects that must be merged into one canonical object key.
 */
export interface ObjectMergeRule extends RuleMeta {
  kind: 'objectMerge';
  /** Pattern resolving to the parent that owns both legacy and canonical keys. */
  base: string;
  /** Legacy object keys to merge (shallow) into `toKey`. */
  fromKeys: string[];
  /** Canonical object key. */
  toKey: string;
}

/**
 * A `kind` enum value rename, disambiguated by the required sibling keys that
 * identify the V2 element (so Backdrop `image`→`photo` is never confused with
 * TapTarget `image`→`icon`).
 */
export interface EnumRenameRule extends RuleMeta {
  kind: 'enumRename';
  /** Sibling keys that must ALL be present to identify this element shape. */
  identifiedBy: string[];
  /** The enum field name (usually `kind`). */
  enumKey: string;
  /** legacyValue → canonicalValue. Only fires when the current value is a legacy key. */
  valueMap: Record<string, string>;
}

/**
 * A key rename on an identified shape (e.g. AssetReference `origin`→`original`,
 * or any element's `styles`→`appearance`). Migrate value→canonical key, delete
 * legacy key (merging if canonical already exists — canonical wins per AC-012).
 */
export interface KeyRenameRule extends RuleMeta {
  kind: 'keyRename';
  /**
   * Shape guard. The node qualifies when it is reached via a parent key in
   * `underParentKey` (e.g. asset values live under a `translations` map), or
   * when `anyElement` is true (styles→appearance applies to any object that has
   * the legacy key).
   */
  underParentKey?: string;
  anyElement?: boolean;
  legacyKey: string;
  canonicalKey: string;
}

/**
 * Move a child key from one sibling container object to another, on shape-identified
 * nodes. Used to relocate a label's padding from the renderer-painted-but-UI-blind
 * `appearance.insets` to the UI-controlled `layout.insets` (the editor's padding
 * control binds to `layout.insets`; the renderer paints BOTH but only strips
 * `appearance.insets` in grid mode). Destination (canonical) wins on conflict UNLESS
 * it is "empty" (for insets: every value 0/absent) — then the legacy value wins so the
 * visible padding is preserved (lossless move, not a strip).
 */
export interface SiblingMoveRule extends RuleMeta {
  kind: 'siblingMove';
  /** Sibling keys that must ALL be present to identify the node (e.g. ['translations'] = a LocalizedLabel). */
  identifiedBy: string[];
  /** Container holding the legacy child (e.g. 'appearance'). */
  fromContainer: string;
  /** Destination container, created if absent (e.g. 'layout'). */
  toContainer: string;
  /** Child key to move (e.g. 'insets'). */
  childKey: string;
  /** When true, an all-zero/empty dest child counts as absent so the legacy value wins (insets semantics). */
  emptyDestMeansAbsent?: boolean;
  /**
   * Skip the node when it is reached via this parent key (shape guard). Used to
   * exclude ProductBadge nodes (always the value under a `badge` key) from
   * `label.insets`: a badge is structurally a label (has `translations`) but its
   * renderer wraps the layout node in `display:contents`, so relocating its
   * padding to `layout.insets` would make it INERT (Badge.tsx:69,76).
   */
  notUnderParentKey?: string;
}

/**
 * A legacy field with NO canonical V2 home (a "Class B" drop/unify orphan, e.g.
 * `copy.subtitle2` after V1 `subtitle1`+`subtitle2` were unified into V2
 * `subtitle`). Detected by the validator and STRIPPED on apply (dry-run shows
 * each deletion for review). `lossy: true` marks that authored content is lost.
 */
export interface DropFieldRule extends RuleMeta {
  kind: 'dropField';
  /** Pattern resolving to the owning object(s). */
  base: string;
  /** Field keys (or dotted subpaths) under base to drop. */
  fields: string[];
  lossy: boolean;
}

export type ConformanceRule =
  | LeafMoveRule
  | ObjectMergeRule
  | EnumRenameRule
  | KeyRenameRule
  | SiblingMoveRule
  | DropFieldRule;

// ── PATH rules: progress indicator (HeaderBar/PageIndicator/DashesPageIndicator) ──
//
// Canonical ProgressIndicator is a FLAT type (dist-v2/models/ProgressIndicator.d.ts):
// { kind, value, activeColor, trackColor, lineLayout, numberOfDashes, dashHeight,
//   dashesSpacing, ... }. Every nested lineAppearance.*/dashesAppearance.*/appearance.*
// read in the renderers is therefore a legacy shape.

const PROGRESS_INDICATOR_RULES: ConformanceRule[] = [
  // The whole-object merge must run BEFORE the leaf moves below.
  {
    kind: 'objectMerge',
    id: 'header.progressIndicator.merge',
    concept: 'Legacy pageIndicator/dashesIndicator merge into progressIndicator',
    base: 'header',
    fromKeys: ['pageIndicator', 'dashesIndicator'],
    toKey: 'progressIndicator',
    evidence: 'src/modules/screen-view-v2/components/shared/HeaderBar/HeaderBar.tsx:72-77',
  },
  {
    kind: 'leafMove',
    id: 'header.progressIndicator.kind',
    concept: 'Legacy header.pageIndicatorKind → progressIndicator.kind',
    base: 'header',
    canonicalLeaf: 'progressIndicator.kind',
    legacyLeaves: ['pageIndicatorKind'],
    evidence: 'src/modules/screen-view-v2/components/shared/HeaderBar/HeaderBar.tsx:75',
  },
  {
    kind: 'leafMove',
    id: 'progressIndicator.activeColor',
    concept: 'Active/filled color → flat activeColor',
    base: 'header.progressIndicator',
    canonicalLeaf: 'activeColor',
    legacyLeaves: ['lineAppearance.color', 'appearance.color', 'dashesAppearance.filledColor', 'appearance.filledColor'],
    evidence: 'PageIndicator.tsx:33 + DashesPageIndicator.tsx:75',
  },
  {
    kind: 'leafMove',
    id: 'progressIndicator.trackColor',
    concept: 'Track/unfilled color → flat trackColor',
    base: 'header.progressIndicator',
    canonicalLeaf: 'trackColor',
    legacyLeaves: ['lineAppearance.trackColor', 'appearance.trackColor', 'dashesAppearance.notFilledColor', 'appearance.notFilledColor'],
    evidence: 'PageIndicator.tsx:32 + DashesPageIndicator.tsx:76',
  },
  {
    kind: 'leafMove',
    id: 'progressIndicator.dashHeight',
    concept: 'Dash height → flat dashHeight',
    base: 'header.progressIndicator',
    canonicalLeaf: 'dashHeight',
    legacyLeaves: ['dashesAppearance.dashHeight', 'appearance.dashHeight'],
    evidence: 'DashesPageIndicator.tsx:73',
  },
  {
    kind: 'leafMove',
    id: 'progressIndicator.dashesSpacing',
    concept: 'Dashes spacing → flat dashesSpacing',
    base: 'header.progressIndicator',
    canonicalLeaf: 'dashesSpacing',
    legacyLeaves: ['dashesAppearance.dashesSpacing', 'appearance.dashesSpacing'],
    evidence: 'DashesPageIndicator.tsx:74',
  },
  {
    kind: 'leafMove',
    id: 'progressIndicator.lineLayout',
    concept: 'Indicator layout → flat lineLayout',
    base: 'header.progressIndicator',
    canonicalLeaf: 'lineLayout',
    legacyLeaves: ['layout'],
    evidence: 'PageIndicator.tsx:34',
  },
];

// ── SHAPE rules: recur at many paths ────────────────────────────────────────

const SHAPE_RULES: ConformanceRule[] = [
  // AssetReference: V1 `{ origin, small, thumb?, medium?, normal? }` → V2 `{ original, small }`.
  // Asset values only ever appear as the values of a `translations` map.
  {
    kind: 'keyRename',
    id: 'asset.origin',
    concept: 'AssetReference origin → original',
    underParentKey: 'translations',
    legacyKey: 'origin',
    canonicalKey: 'original',
    evidence: 'V2OptionsListSection.tsx:172,311 + schema-v2/MIGRATION.md:247-251',
  },
  // V1 element styling lived under `styles`; V2 uses `appearance`. A bare object
  // key literally named `styles` never exists on a conformant V2 element.
  {
    kind: 'keyRename',
    id: 'element.styles',
    concept: 'Element styles → appearance',
    anyElement: true,
    legacyKey: 'styles',
    canonicalKey: 'appearance',
    evidence: 'schema-v2/MIGRATION.md:50 (Text.styles→TypographyStyle) + naming map (.styles.*→.appearance.*)',
  },
  // Text kind enum (LocalizedLabel): identified by `translations`.
  {
    kind: 'enumRename',
    id: 'text.kind',
    concept: 'Text kind default/template → static/dynamic',
    identifiedBy: ['translations'],
    enumKey: 'kind',
    valueMap: { default: 'static', template: 'dynamic' },
    evidence: 'schema-v2/MIGRATION.md:230-231',
  },
  // Backdrop kind enum: identified by `content` + legacy value domain {color,image,video}.
  {
    kind: 'enumRename',
    id: 'backdrop.kind',
    concept: 'Backdrop kind color/image/video → solid/photo/clip',
    identifiedBy: ['content'],
    enumKey: 'kind',
    valueMap: { color: 'solid', image: 'photo', video: 'clip' },
    evidence: 'schema-v2/MIGRATION.md:232-234',
  },
  // TapTarget kind enum: identified by `action` + legacy value domain {text,image}.
  {
    kind: 'enumRename',
    id: 'taptarget.kind',
    concept: 'TapTarget kind text/image → label/icon',
    identifiedBy: ['action'],
    enumKey: 'kind',
    valueMap: { text: 'label', image: 'icon' },
    evidence: 'schema-v2/MIGRATION.md:235',
  },
  // ── Paywall / options enums the V1→V2 converter used to leak (added after they were found in
  // live dev AND prod data). Two of these are not cosmetic — the renderer silently does the WRONG
  // thing, so a document carrying them looks fine in every schema-free check:
  //   · elementsOrder: V1 and V2 use the SAME names, but the converter renamed PurchaseFirst →
  //     'LabelBelowButton' (a value in NEITHER schema). PaywallFooter.tsx:55 recognises only
  //     'PurchaseFirst' → the footer renders INVERTED (auto-renew label above the purchase button).
  //   · closeVisibility: PaywallHeader.tsx:88 honours only 'delayedVisible', so V1's
  //     'visibleAfterTimer' shows the close button IMMEDIATELY instead of after the timer.
  {
    kind: 'enumRename',
    id: 'paywall.elementsOrder',
    concept: 'Paywall footer order LabelBelowButton/LabelAboveButton (non-existent) → PurchaseFirst/AutoRenewLabelFirst',
    identifiedBy: ['elementsOrder'],
    enumKey: 'elementsOrder',
    valueMap: { LabelBelowButton: 'PurchaseFirst', LabelAboveButton: 'AutoRenewLabelFirst' },
    evidence: 'FooterElementOrder enum (openapi-v2.yaml:4438) + PaywallFooter.tsx:55 (isPurchaseFirst) — renders inverted today',
  },
  {
    kind: 'enumRename',
    id: 'paywall.closeVisibility',
    concept: 'Paywall close button visibleAfterTimer (V1) → delayedVisible',
    identifiedBy: ['closeVisibility'],
    enumKey: 'closeVisibility',
    valueMap: { visibleAfterTimer: 'delayedVisible' },
    evidence: 'PaywallHeader.tsx:88 (only delayedVisible delays the close button)',
  },
  {
    kind: 'enumRename',
    id: 'paywall.viewKind',
    concept: 'ProductCatalog viewKind "list" (never a V1 nor V2 value — an invented default) → Vertical',
    identifiedBy: ['viewKind', 'items'],
    enumKey: 'viewKind',
    valueMap: { list: 'Vertical', horizontal: 'Horizontal', vertical: 'Vertical' },
    evidence: 'CatalogViewKind enum (openapi-v2.yaml:4385); ProductCatalog.tsx treats non-Horizontal as vertical → same pixels',
  },
  {
    kind: 'enumRename',
    id: 'paywall.productLayout',
    concept: 'ProductOffer layout SubscriptionListItemTypeN (V1) → ProductLayoutN',
    identifiedBy: ['productLayout'],
    enumKey: 'productLayout',
    valueMap: {
      SubscriptionListItemType1: 'ProductLayout1',
      SubscriptionListItemType2: 'ProductLayout2',
      SubscriptionListItemType3: 'ProductLayout3',
      SubscriptionListItemType4: 'ProductLayout4',
      SubscriptionListItemType5: 'ProductLayout5',
      SubscriptionListItemType6: 'ProductLayout6',
      SubscriptionListItemType7: 'ProductLayout7',
      SubscriptionListItemType8: 'ProductLayout8',
      SubscriptionListItemType9: 'ProductLayout9',
    },
    evidence: 'ProductLayout enum (openapi-v2.yaml:4368)',
  },
  {
    kind: 'enumRename',
    id: 'paywall.currencyFormat',
    concept: 'Paywall currencyFormat CurrencyISOCode/CurrencySymbol (V1) → Currency',
    identifiedBy: ['currencyFormat'],
    enumKey: 'currencyFormat',
    valueMap: { CurrencyISOCode: 'Currency', CurrencySymbol: 'Currency' },
    evidence: 'CurrencyFormat enum (openapi-v2.yaml:4358); no web reader — iOS only',
  },
  {
    kind: 'enumRename',
    id: 'stepper.kind',
    concept: 'Stepper kind stepperSlotN (an older converter\'s invention) → the real V2 StepperKind',
    identifiedBy: ['timer', 'items'],
    enumKey: 'kind',
    valueMap: { stepperSlot1: 'imageContainerFirst', stepperSlot2: 'chartContainerFirst' },
    evidence: 'StepperKind enum (openapi-v2.yaml:4261) = orbital|imageContainerFirst|chartContainerFirst; v2StepperConfig.ts offers exactly those three',
  },
  {
    kind: 'enumRename',
    id: 'options.itemLayout',
    concept: 'OptionsList itemLayout "Tittle" (a V1 typo) → SmallImageTitle',
    identifiedBy: ['itemLayout', 'items'],
    enumKey: 'itemLayout',
    valueMap: { Tittle: 'SmallImageTitle' },
    // NOT 'Title': Title → TitleOnly has no image slot (OptionsItem.tsx), and every item carrying
    // this layout in the live data has an image. Sibling twoCols templates use SmallImageTitle.
    evidence: 'ItemLayout enum (openapi-v2.yaml:4318) has no "Tittle"; V1 TwoColumn*ListItemType (openapi.yaml:3194,3204) does',
  },
  // Label padding: the V2 Text renderer paints BOTH `<label>.appearance.insets`
  // (inner text div, mapTypographyStyles) and `<label>.layout.insets` (outer div,
  // mapFrameLayoutStyles) as CSS padding, but the editor's per-label padding control
  // binds ONLY to `layout.insets`. Legacy/migrated labels (V1 paddingLeft/Right →
  // appearance.insets) thus render a hidden padding the UI cannot change. Relocate it
  // to the UI-controlled `layout.insets`. Scoped to LocalizedLabel nodes (those with
  // `translations`) so section/container `appearance.insets` (options/roller/paywall,
  // which IS UI-controlled) is never touched. MUST be last in SHAPE_RULES so the
  // `styles`→`appearance` rename runs first.
  {
    kind: 'siblingMove',
    id: 'label.insets',
    concept: 'Label padding appearance.insets (renderer-painted, UI-blind) → layout.insets (UI-controlled)',
    identifiedBy: ['translations'],
    fromContainer: 'appearance',
    toContainer: 'layout',
    childKey: 'insets',
    emptyDestMeansAbsent: true,
    notUnderParentKey: 'badge',
    evidence: 'Text.tsx:68,84,153,159 (appearance.insets→inner div / layout.insets→outer div via mapEdgeInsetsToCSS) + TitleSubtitle.tsx:44-45 (strips appearance.insets ONLY in grid) + v2OptionsListStyleConfig.ts:594 (UI "Title padding" → layout.insets); ProductBadge excluded (Badge.tsx:69,76 layout wrapper is display:contents → inert)',
  },
];

// ── PATH rules: CopyBlock (Class B — dropped/unified fields) ─────────────────
//
// V2 CopyBlock has ONLY { title, subtitle, animation, stagger } (openapi-v2.yaml
// CopyBlock). V1's subtitle1+subtitle2 were unified into the single `subtitle`.
// Scoped to `copy` so legitimate WebEmbed/FlexibleLayout section `subtitle2`
// (the customScreenBridge {title, subtitle, subtitle2}) is never touched.

const COPY_BLOCK_RULES: ConformanceRule[] = [
  {
    kind: 'leafMove',
    id: 'copy.subtitle1',
    concept: 'Legacy copy.subtitle1 → copy.subtitle',
    base: 'copy',
    canonicalLeaf: 'subtitle',
    legacyLeaves: ['subtitle1'],
    evidence: 'schema-v2/MIGRATION.md:214 (title+subtitle1+subtitle2 → copy) + openapi-v2.yaml CopyBlock',
  },
  {
    kind: 'dropField',
    id: 'copy.subtitle2',
    concept: 'Orphan copy.subtitle2 (no V2 CopyBlock field) — stripped on apply',
    base: 'copy',
    fields: ['subtitle2'],
    lossy: true,
    evidence: 'CopyBlock has no subtitle2 (openapi-v2.yaml) + CopyBlock.tsx renders only title/subtitle',
  },
];

/**
 * The complete ordered rule set. Order matters for the normalizer:
 * objectMerge runs before the leafMove rules that target the merged object.
 */
export const CONFORMANCE_RULES: ConformanceRule[] = [
  ...PROGRESS_INDICATOR_RULES,
  ...COPY_BLOCK_RULES,
  ...SHAPE_RULES,
];

/** All rule ids, for test cross-checks and report legends. */
export const CONFORMANCE_RULE_IDS: string[] = CONFORMANCE_RULES.map((r) => r.id);
